import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/admin/ImageUpload";

/**
 * Group of related content fields, rendered as a titled card. Purely visual.
 */
export function ContentGroup({ title, description, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-5">
      <div className="mb-1 text-sm font-heading font-bold text-slate-800">{title}</div>
      {description && <p className="mb-4 text-xs text-slate-500">{description}</p>}
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/**
 * A single CMS field. Renders an <Input> or <Textarea> depending on `rows`.
 * `content` = the CMS object (e.g. settings.home_content), `setContent` sets it,
 * `defaults` = default values from cms-defaults.js.
 */
export function CmsField({
  content,
  setContent,
  defaults,
  fieldKey,
  label,
  helper,
  rows = 0,
  full = false,
  placeholder,
}) {
  const val = content?.[fieldKey] ?? "";
  const ph = placeholder ?? defaults?.[fieldKey] ?? "";
  const testId = `cms-${fieldKey.replace(/_/g, "-")}`;
  const cls = full ? "sm:col-span-2" : "";
  const update = (v) => setContent({ ...content, [fieldKey]: v });
  return (
    <div className={cls}>
      <Label htmlFor={testId} className="text-xs font-semibold text-slate-700">
        {label}
      </Label>
      {rows > 0 ? (
        <Textarea
          id={testId}
          rows={rows}
          value={val}
          onChange={(e) => update(e.target.value)}
          placeholder={ph}
          data-testid={testId}
        />
      ) : (
        <Input
          id={testId}
          value={val}
          onChange={(e) => update(e.target.value)}
          placeholder={ph}
          data-testid={testId}
        />
      )}
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

/**
 * Image field with upload + URL fallback.
 */
export function CmsImage({ content, setContent, defaults, fieldKey, label, folder = "content", helper, full = true }) {
  const val = content?.[fieldKey] ?? "";
  const testId = `cms-${fieldKey.replace(/_/g, "-")}`;
  const cls = full ? "sm:col-span-2" : "";
  return (
    <div className={cls}>
      <ImageUpload
        label={label}
        value={val || defaults?.[fieldKey]}
        onChange={(v) => setContent({ ...content, [fieldKey]: v })}
        folder={folder}
        testId={testId}
        helper={helper || "Upload a new image or paste any URL/local path (e.g. /images/hero-scientist.jpg)."}
      />
    </div>
  );
}
